import { parseDmsCoordinate, DMS_COORDINATE_RE } from '@ooh/contracts';

export interface Coordinates {
  lat: number;
  lng: number;
}

/** Maximum absolute latitude in decimal degrees; valid latitudes are [-90, 90]. */
export const LAT_MAX = 90;
/** Maximum absolute longitude in decimal degrees; valid longitudes are [-180, 180]. */
export const LNG_MAX = 180;

/** A lone cardinal direction letter (a standalone prefix token like "N"). */
const LONE_CARDINAL = /^[NSEWnsew]$/;

/** A signed decimal magnitude with an optional cardinal prefix and/or suffix. */
const NUMBER_TOKEN = /^([NSEWnsew]?)([+-]?\d+(?:\.\d+)?)([NSEWnsew]?)$/;

/** Minute/second markers that confirm genuine DMS format. */
const DMS_MINUTE_SECOND_MARKERS = /['"\u2032\u2033]/;

/**
 * Returns true when the input has both a degree symbol AND minute/second markers,
 * indicating a genuine DMS coordinate pair. Inputs with just a degree symbol
 * (e.g. "52.2297°N") are decimal-degrees and handled by the decimal path.
 */
function looksLikeDmsPair(input: string): boolean {
  return input.includes('\u00b0') && DMS_MINUTE_SECOND_MARKERS.test(input);
}

/** Returns true when the direction letter belongs to the latitude axis (N/S). */
const isLatDir = (d: string): boolean => d === 'N' || d === 'S';
/** Returns true when the direction letter belongs to the longitude axis (E/W). */
const isLngDir = (d: string): boolean => d === 'E' || d === 'W';

/**
 * Validates that `lat` and `lng` are within their legal ranges.
 * Returns `{ lat, lng }` on success, `null` on range violation.
 */
function validatedCoords(lat: number, lng: number): Coordinates | null {
  if (Math.abs(lat) > LAT_MAX || Math.abs(lng) > LNG_MAX) return null;
  return { lat, lng };
}

/**
 * Assigns lat/lng from two decimal values and their cardinal direction letters.
 * Returns null when both values share the same axis (both N/S or both E/W).
 */
function assignLatLng(decA: number, decB: number, dirA: string, dirB: string): Coordinates | null {
  if (isLatDir(dirA) && isLngDir(dirB)) return validatedCoords(decA, decB);
  if (isLngDir(dirA) && isLatDir(dirB)) return validatedCoords(decB, decA);
  // Same-axis pair (both N/S or both E/W) → ambiguous, reject.
  if ((isLatDir(dirA) && isLatDir(dirB)) || (isLngDir(dirA) && isLngDir(dirB))) return null;
  // No direction letters on either: first = lat, second = lng.
  return validatedCoords(decA, decB);
}

/**
 * Builds the pair-match regex from the shared DMS_COORDINATE_RE source.
 * Group layout: 1=rawA, 2=dirPrefixA, …6=dirSuffixA, 7=rawB, 8=dirPrefixB, …12=dirSuffixB
 */
function buildDmsPairRegex(): RegExp {
  const axisSrc = DMS_COORDINATE_RE.source.replace(/^\^/, '').replace(/\$$/, '');
  return new RegExp(`^\\s*(${axisSrc})\\s*[,;\\s]+\\s*(${axisSrc})\\s*$`, 'i');
}

/**
 * Parses a DMS coordinate pair of the form "52°13'46"N 16°7'24"E" into
 * `{ lat, lng }`. The two components must be separated by whitespace or a
 * comma/semicolon, and each component must parse via the single-axis DMS
 * parser from @ooh/contracts.
 *
 * Direction letters determine lat/lng assignment:
 *  - N/S → latitude
 *  - E/W → longitude
 *
 * Returns null when parsing fails or the pair is ambiguous.
 */
function parseDmsPair(input: string): Coordinates | null {
  const pairMatch = buildDmsPairRegex().exec(input.trim());
  if (!pairMatch) return null;

  const rawA = pairMatch[1];
  const rawB = pairMatch[7];
  if (!rawA || !rawB) return null;

  const decA = parseDmsCoordinate(rawA.trim());
  const decB = parseDmsCoordinate(rawB.trim());
  if (decA === null || decB === null) return null;

  // Determine cardinal direction from groups already captured by dmsPairRe.
  const dirA = (pairMatch[2] ?? pairMatch[6] ?? '').toUpperCase();
  const dirB = (pairMatch[8] ?? pairMatch[12] ?? '').toUpperCase();

  return assignLatLng(Number(decA), Number(decB), dirA, dirB);
}

/**
 * Parses a free-text decimal-degrees coordinate pair into `{ lat, lng }`, or
 * returns null when the input is not an unambiguous coordinate pair so callers
 * can fall back to text search.
 *
 * Accepts dot decimals ("52.2297, 16.1234"), comma decimals
 * ("52,2297, 16,1234" / "52,2297 16,1234"), comma / whitespace / semicolon
 * separators, optional surrounding whitespace, a leading "+", an optional degree
 * symbol, and cardinal directions N/S/E/W as a prefix or suffix (S and W are
 * negative). Latitude must be in [-90, 90] and longitude in [-180, 180].
 *
 * Also accepts DMS coordinate pairs like "52°13'46\"N 16°7'24\"E".
 */
export function parseCoordinates(input: string): Coordinates | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;

  // If the input has degree symbols AND minute/second markers, it is DMS — try
  // DMS pair parsing. Inputs with only a degree symbol (e.g. "52.2297°N") fall
  // through to the decimal path which strips the degree symbol correctly.
  if (looksLikeDmsPair(trimmed)) {
    return parseDmsPair(trimmed);
  }

  // Strip degree symbols so a "52.2297°N" suffix stays attached to its number.
  const withoutDegrees = trimmed.replace(/\u00b0/g, '');

  // Split on every supported separator: comma, semicolon, and whitespace.
  const rawTokens = withoutDegrees.split(/[\s,;]+/).filter((token) => token.length > 0);
  // Merge a lone leading cardinal ("N") into the number that follows it so
  // "N 52.2297 E 16.1234" reduces to ["N52.2297", "E16.1234"].
  const merged = mergeLoneCardinals(rawTokens);
  if (merged === null) return null;

  // Comma-as-decimal vs comma-as-separator: with no dot decimals present, four
  // integer tokens pair into two comma-decimal numbers ("52,2297, 16,1234").
  const tokens = withoutDegrees.includes('.') ? merged : pairCommaDecimals(merged);
  if (tokens === null) return null;

  const parsed = tokens.map(parseToken);
  if (parsed.length !== 2 || parsed.some((value) => value === null)) return null;

  const [lat, lng] = parsed as [number, number];
  if (Math.abs(lat) > LAT_MAX || Math.abs(lng) > LNG_MAX) return null;

  return { lat, lng };
}

/**
 * Folds standalone cardinal tokens (e.g. "N") onto the immediately following
 * token so they parse as a prefixed coordinate. Returns null when a lone
 * cardinal has no following token to attach to.
 */
function mergeLoneCardinals(tokens: string[]): string[] | null {
  const merged: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i] as string;
    if (LONE_CARDINAL.test(token)) {
      const next = tokens[i + 1];
      if (next === undefined) return null;
      merged.push(token + next);
      i++;
      continue;
    }
    merged.push(token);
  }
  return merged;
}

/** A token that is an integer magnitude with an optional cardinal prefix/suffix. */
const INTEGER_TOKEN = /^([NSEWnsew]?)([+-]?\d+)([NSEWnsew]?)$/;

/**
 * Resolves comma-decimal input that arrived as four integer tokens (the Polish
 * "52,2297, 16,1234" form, where commas are decimals). Pairs (a, b) -> "a.b".
 * Two tokens are left unchanged (already a separated pair). Any other arity, or
 * a non-integer token in the four-token case, returns null.
 */
function pairCommaDecimals(tokens: string[]): string[] | null {
  if (tokens.length === 2) return tokens;
  if (tokens.length === 4 && tokens.every((token) => INTEGER_TOKEN.test(token))) {
    return [`${tokens[0]}.${tokens[1]}`, `${tokens[2]}.${tokens[3]}`];
  }
  return tokens;
}

/**
 * Parses a single token into a signed number, applying a cardinal direction
 * (prefix or suffix) where present. Returns null for anything that is not a
 * lone number or number-with-cardinal.
 */
function parseToken(token: string): number | null {
  const match = NUMBER_TOKEN.exec(token);
  if (match === null) return null;
  const [, prefix, numberPart, suffix] = match;
  if (prefix && suffix) return null; // cardinal on both ends is invalid
  const cardinal = (prefix || suffix || '').toUpperCase();
  const magnitude = Number(numberPart);
  if (Number.isNaN(magnitude)) return null;
  if (cardinal === 'S' || cardinal === 'W') return -Math.abs(magnitude);
  return magnitude;
}
